import type { RouterConfig } from '@nuxt/schema'
import { unifiedRoutes, type GeneratedRoute } from '../../generated/routes'
import { RoutingDefinitions } from '../generator/types/RoutingDefinitions'
import { IRouteMeta } from "@app/interfaces/dtos/IRouteMeta";
import { checkIsMobile } from '@app/composables/useMobile';
import type { RouteLocationNormalized } from 'vue-router';

// Separate organization routes from other routes
const omniSearchRoutes: GeneratedRoute[] = []
const organizationRoutes: GeneratedRoute[] = []
const otherRoutes: GeneratedRoute[] = []

unifiedRoutes.forEach((route) => {
	if (route.path.includes('omni-search')) {
		omniSearchRoutes.push(route)
	}
	else if (route.name?.includes('Organisation')) {
		organizationRoutes.push(route)
	} else {
		otherRoutes.push(route)
	}
})

// Reorder: other routes first, organization routes last (lower priority)
const orderedRoutes = [...otherRoutes, ...organizationRoutes, ...omniSearchRoutes]

const computedRoutes: {
	name: string
	path: string
	meta: IRouteMeta
	component: () => Promise<any>
}[] = []

type RouteQueryValue = RouteLocationNormalized['query'][string];

const normalizeValue = (value: unknown): string | string[] => {
	if (Array.isArray(value)) return value.map(String);
	if (value == null) return '';
	// eslint-disable-next-line @typescript-eslint/no-base-to-string
	return String(value);
};

const normalizeRecord = (record: Record<string, unknown>): string => {
	const entries = Object.keys(record)
		.sort()
		.map((key) => [key, normalizeValue(record[key])] as const);
	return JSON.stringify(entries);
};

const getPageQuery = (route: RouteLocationNormalized): string | undefined => {
	const pageValue = route.query.page as RouteQueryValue;

	if (Array.isArray(pageValue)) return pageValue[0] || undefined;
	if (pageValue == null) return undefined;

	return String(pageValue);
};

const isPaginationOnlyChange = (to: RouteLocationNormalized, from: RouteLocationNormalized): boolean => {
	if (getPageQuery(to) === getPageQuery(from)) return false;

	const toQuery = Object.fromEntries(
		Object.entries(to.query as Record<string, unknown>)
			.filter(([key]) => key !== 'page')
	);
	const fromQuery = Object.fromEntries(
		Object.entries(from.query as Record<string, unknown>)
			.filter(([key]) => key !== 'page')
	);

	const querySameWithoutPage = normalizeRecord(toQuery) === normalizeRecord(fromQuery);

	const paramsSame = normalizeRecord(to.params as Record<string, unknown>) ===
		normalizeRecord(from.params as Record<string, unknown>);

	const routeSame = to.name === from.name && to.hash === from.hash;

	return querySameWithoutPage && paramsSame && routeSame;
};

orderedRoutes.forEach((route) => {
	// Use specific component for catch-all route, otherwise use _gen.vue
	const component = route.name as RoutingDefinitions === RoutingDefinitions.CATCH_ALL
		? (): Promise<any> => import('./pages/[...catchall].vue')
		: (): Promise<any> => import('./pages/_gen.vue');

	computedRoutes.push({
		name: route.name,
		path: route.path,
		meta: route.meta,
		component,
	})
})

export default {
	routes: (scanned) => {
		const catchAllRoute = scanned.find(route => route.path === '/:catchall(.*)*')
		const normalizedScanned = scanned.filter(route => route.path !== '/:catchall(.*)*')

		return [
			...normalizedScanned,
			...computedRoutes,
			...(catchAllRoute ? [{ ...catchAllRoute, name: RoutingDefinitions.CATCH_ALL }] : [])
		]
	},

	scrollBehavior: (to, from, saved) => {
		if (checkIsMobile()) return { top: 0 };
		if (saved) return saved;

		return isPaginationOnlyChange(to, from) ? { top: 0 } : false;
	}
} as RouterConfig
